import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Tooltip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ShareIcon from "@mui/icons-material/Share";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import { useAuth } from "../../firebase/authContext";
import { useSocial } from "../../context/SocialContext";
import {
  getAllUsers,
  updateUserStatus,
  updateUserRole,
  getAllNotesForAdmin,
  reviewNote,
  moderateNote,
  adminDeleteNote,
} from "../../firebase/adminService";
import { cleanVerseQuoteText } from "../../bible/verseQuote";

const AdminPortal = () => {
  const { currentUser, isAdmin } = useAuth();
  const { openUserCard } = useSocial();
  const [activeTab, setActiveTab] = useState(0);

  // States
  const [users, setUsers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [noteSearch, setNoteSearch] = useState("");
  const [toastMsg, setToastMsg] = useState("");
  const [previewNote, setPreviewNote] = useState(null);
  const [rejectTargetNote, setRejectTargetNote] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [reviewingId, setReviewingId] = useState("");

  const loadData = async () => {
    const [uList, nList] = await Promise.all([
      getAllUsers(),
      getAllNotesForAdmin(),
    ]);
    setUsers(uList);
    setNotes(nList);
  };

  useEffect(() => {
    loadData();
  }, [isAdmin]);

  const handleToggleAdminStatus = async (user) => {
    const nextRole = user.role === "admin" ? "user" : "admin";
    const ok = await updateUserRole(user.id || user.uid, nextRole);
    if (ok) {
      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id || u.uid === user.uid ? { ...u, role: nextRole } : u
        )
      );
      setToastMsg(`用户角色已更新为：${nextRole === "admin" ? "管理员" : "普通用户"}`);
    }
  };

  const handleToggleUserDisabled = async (user) => {
    const nextStatus = user.status === "disabled" ? "active" : "disabled";
    const ok = await updateUserStatus(user.id || user.uid, nextStatus);
    if (ok) {
      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id || u.uid === user.uid ? { ...u, status: nextStatus } : u
        )
      );
      setToastMsg(`用户状态已更改为：${nextStatus === "active" ? "正常" : "已停用"}`);
    }
  };

  const handleToggleFeatureNote = async (note) => {
    const nextFeatured = !note.featured;
    const ok = await moderateNote(note.id, { featured: nextFeatured });
    if (ok) {
      setNotes((prev) =>
        prev.map((n) => (n.id === note.id ? { ...n, featured: nextFeatured } : n))
      );
      setToastMsg(nextFeatured ? "已将该笔记设为精选推荐！" : "已取消精选推荐");
    }
  };

  const handleUnshareNote = async (note) => {
    if (!window.confirm("确定要下架该笔记的公开分享吗？（作者仍可在私密笔记中查看）"))
      return;
    const ok = await moderateNote(note.id, { isPublic: false });
    if (ok) {
      setNotes((prev) =>
        prev.map((n) => (n.id === note.id ? { ...n, isPublic: false } : n))
      );
      setToastMsg("已成功下架公开分享！");
    }
  };

  const handleDeleteNote = async (note) => {
    if (!window.confirm("确定要永久删除该笔记吗？此操作不可撤销！")) return;
    const ok = await adminDeleteNote(note.id);
    if (ok) {
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
      setToastMsg("笔记已永久删除");
    }
  };

  const handleApproveNote = async (note) => {
    setReviewingId(note.id);
    try {
      const ok = await reviewNote(note.id, { action: "approve" });
      if (ok) {
        setNotes((prev) =>
          prev.map((n) =>
            n.id === note.id
              ? { ...n, isPublic: true, reviewStatus: "approved", reviewNote: null }
              : n
          )
        );
        setToastMsg("已审核通过并公开展示！");
      }
    } finally {
      setReviewingId("");
    }
  };

  const handleConfirmRejectNote = async () => {
    if (!rejectTargetNote) return;
    const target = rejectTargetNote;
    const reason = rejectReason.trim() || "内容未通过公开审核";
    setReviewingId(target.id);
    try {
      const ok = await reviewNote(target.id, { action: "reject", reason });
      if (ok) {
        setNotes((prev) =>
          prev.map((n) =>
            n.id === target.id
              ? { ...n, isPublic: false, reviewStatus: "rejected", reviewNote: reason }
              : n
          )
        );
        setToastMsg("已驳回该笔记的公开申请");
        setRejectTargetNote(null);
        setRejectReason("");
      }
    } finally {
      setReviewingId("");
    }
  };

  const handleBatchApprove = async (list) => {
    if (!Array.isArray(list) || !list.length) return;
    for (const item of list) {
      await reviewNote(item.id, { action: "approve" });
    }
    setNotes((prev) =>
      prev.map((n) =>
        n.reviewStatus === "pending"
          ? { ...n, isPublic: true, reviewStatus: "approved", reviewNote: null }
          : n
      )
    );
    setToastMsg(`已批量通过 ${list.length} 条待审核笔记！`);
  };

  // Filtered lists
  const filteredUsers = users.filter((u) => {
    if (!userSearch.trim()) return true;
    const q = userSearch.toLowerCase();
    return (
      (u.email || "").toLowerCase().includes(q) ||
      (u.displayName || "").toLowerCase().includes(q) ||
      (u.uid || "").toLowerCase().includes(q)
    );
  });

  const filteredSharedNotes = notes.filter((n) => {
    if (!noteSearch.trim()) return true;
    const q = noteSearch.toLowerCase();
    return (
      (n.title || "").toLowerCase().includes(q) ||
      (n.content || "").toLowerCase().includes(q) ||
      (n.userDisplayName || "").toLowerCase().includes(q) ||
      (n.bookName || "").toLowerCase().includes(q)
    );
  });

  const sharedNotesCount = notes.filter((n) => Boolean(n.isPublic)).length;
  const pendingNotes = notes.filter((n) => n.reviewStatus === "pending");

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <AdminPanelSettingsIcon color="secondary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              系统后台管理端
            </Typography>
            <Typography variant="body2" color="text.secondary">
              读者用户管理、灵修分享审查与内容治理
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            刷新数据
          </Button>
        </Box>
      </Box>

      {!currentUser && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          提示：您当前尚未登录，请点击右上角「登录 / 注册」登录管理员账号后进入。
        </Alert>
      )}

      {currentUser && !isAdmin && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          访问受限：当前登录账号（{currentUser.email || currentUser.displayName}）未具有系统管理员权限。管理员权限需在数据库中配置。
        </Alert>
      )}

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          textColor="secondary"
          indicatorColor="secondary"
          variant="fullWidth"
        >
          <Tab
            icon={<DashboardIcon fontSize="small" />}
            iconPosition="start"
            label="数据概览"
            sx={{ fontWeight: 700 }}
          />
          <Tab
            icon={<PeopleAltIcon fontSize="small" />}
            iconPosition="start"
            label={`用户管理 (${users.length})`}
            sx={{ fontWeight: 700 }}
          />
          <Tab
            icon={<ShareIcon fontSize="small" />}
            iconPosition="start"
            label={`分享内容管理 (${sharedNotesCount})`}
            sx={{ fontWeight: 700 }}
          />
          <Tab
            icon={<FactCheckIcon fontSize="small" />}
            iconPosition="start"
            label={`待审核 (${pendingNotes.length})`}
            sx={{ fontWeight: 700 }}
          />
        </Tabs>
      </Paper>

      {/* Tab 0: Dashboard */}
      {activeTab === 0 && (
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                textAlign: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                总注册用户数
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, my: 1, color: "primary.main" }}>
                {users.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                活跃正常状态：{users.filter((u) => u.status !== "disabled").length}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                textAlign: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                全部灵修笔记
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, my: 1, color: "success.main" }}>
                {notes.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                读者私人及公开心得总汇
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                textAlign: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                公开分享笔记
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, my: 1, color: "secondary.main" }}>
                {sharedNotesCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                展示在社区灵修墙上的数量
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                textAlign: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                精选推荐笔记
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, my: 1, color: "warning.main" }}>
                {notes.filter((n) => Boolean(n.featured)).length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                由管理员加星置顶的优秀心得
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tab 1: User Management */}
      {activeTab === 1 && (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
          <Box sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder="按邮箱、昵称或 UID 搜索用户..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 360 }}
            />
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>用户</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>邮箱</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>角色</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>账号状态</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>注册/最后登录</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => {
                    const name = user.displayName || user.email?.split("@")[0] || "读者";
                    const isUserAdmin = user.role === "admin";
                    const isDisabled = user.status === "disabled";

                    return (
                      <TableRow key={user.id || user.uid} hover>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              cursor: "pointer",
                              "&:hover": { opacity: 0.8 },
                            }}
                            onClick={() =>
                              openUserCard(user.id || user.uid, {
                                displayName: name,
                                photoURL: user.photoURL,
                              })
                            }
                          >
                            <Avatar
                              src={user.photoURL}
                              sx={{
                                width: 28,
                                height: 28,
                                fontSize: "0.8rem",
                                bgcolor: isUserAdmin ? "secondary.main" : "primary.main",
                              }}
                            >
                              {name[0]}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 700, "&:hover": { textDecoration: "underline" } }}>
                              {name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {user.email || "无邮箱"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={isUserAdmin ? "管理员" : "普通读者"}
                            size="small"
                            color={isUserAdmin ? "secondary" : "default"}
                            sx={{ fontWeight: 700, height: 22, fontSize: "0.7rem" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={isDisabled ? "已停用" : "正常"}
                            size="small"
                            color={isDisabled ? "error" : "success"}
                            variant="outlined"
                            sx={{ fontWeight: 700, height: 22, fontSize: "0.7rem" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {user.lastLoginAt ? "已登录活跃" : "本地/新用户"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => handleToggleAdminStatus(user)}
                            sx={{ textTransform: "none", fontSize: "0.75rem", mr: 0.5 }}
                          >
                            {isUserAdmin ? "降为读者" : "设为管理"}
                          </Button>
                          <Button
                            size="small"
                            color={isDisabled ? "success" : "error"}
                            variant="text"
                            onClick={() => handleToggleUserDisabled(user)}
                            sx={{ textTransform: "none", fontSize: "0.75rem" }}
                          >
                            {isDisabled ? "恢复账号" : "停用账号"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      未匹配到用户
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 2: Share Moderation */}
      {activeTab === 2 && (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
          <Box sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder="按标题、感悟、作者或书卷搜索分享笔记..."
              value={noteSearch}
              onChange={(e) => setNoteSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 360 }}
            />
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>笔记标题与经文</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>作者</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>公开状态</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>精选推荐</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>记录日期</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>治理操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSharedNotes.length > 0 ? (
                  filteredSharedNotes.map((note) => {
                    const ref = `${note.bookName || ""} 第${note.chapterNumber || 1}章${
                      note.verseRange ? `:${note.verseRange}` : ""
                    }`;

                    return (
                      <TableRow key={note.id} hover>
                        <TableCell>
                          <Box sx={{ cursor: "pointer" }} onClick={() => setPreviewNote(note)}>
                            <Typography variant="body2" sx={{ fontWeight: 700, "&:hover": { color: "primary.main" } }}>
                              {note.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              出处：{ref}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {note.userDisplayName || "读者"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {note.isPublic ? (
                            <Chip
                              label="公开可见"
                              size="small"
                              color="success"
                              sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700 }}
                            />
                          ) : (
                            <Chip
                              label="私密"
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: "0.68rem" }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleFeatureNote(note)}
                            color={note.featured ? "warning" : "default"}
                          >
                            {note.featured ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {note.updatedAtIso || note.createdAtIso
                              ? new Date(
                                  note.updatedAtIso || note.createdAtIso
                                ).toLocaleDateString()
                              : "近期"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {note.isPublic && (
                            <Tooltip title="下架分享（设为私密）">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleUnshareNote(note)}
                              >
                                <VisibilityOffIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="永久删除违规笔记">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteNote(note)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      暂无相关分享笔记
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 3: Pending Review Queue */}
      {activeTab === 3 && (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              待审核公开笔记与节评 ({pendingNotes.length})
            </Typography>
            {pendingNotes.length > 0 && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircleOutlineIcon />}
                onClick={() => handleBatchApprove(pendingNotes)}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
              >
                全部通过 ({pendingNotes.length})
              </Button>
            )}
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>经文与内容预览</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>作者</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>提交时间</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>审核操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingNotes.length > 0 ? (
                  pendingNotes.map((note) => {
                    const ref = `${note.bookName || ""} 第${note.chapterNumber || 1}章${
                      note.verseRange ? `:${note.verseRange}` : ""
                    }`;
                    return (
                      <TableRow key={note.id} hover>
                        <TableCell>
                          <Box sx={{ cursor: "pointer" }} onClick={() => setPreviewNote(note)}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {note.title}
                            </Typography>
                            <Typography variant="caption" color="primary" sx={{ display: "block" }}>
                              出处：{ref}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5, whiteSpace: "pre-line" }}
                            >
                              {note.content}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {note.userDisplayName || "读者"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {note.updatedAtIso || note.createdAtIso
                              ? new Date(note.updatedAtIso || note.createdAtIso).toLocaleDateString()
                              : "近期"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "inline-flex", gap: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              disabled={reviewingId === note.id}
                              startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                              onClick={() => handleApproveNote(note)}
                              sx={{ textTransform: "none", fontWeight: 700 }}
                            >
                              通过
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={reviewingId === note.id}
                              startIcon={<CancelOutlinedIcon fontSize="small" />}
                              onClick={() => {
                                setRejectTargetNote(note);
                                setRejectReason("");
                              }}
                              sx={{ textTransform: "none", fontWeight: 700 }}
                            >
                              驳回
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      当前没有待审核的公开笔记
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Reject Reason Dialog */}
      <Dialog
        open={Boolean(rejectTargetNote)}
        onClose={() => setRejectTargetNote(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>驳回公开申请</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            请填写未通过原因（作者可在自己的笔记/节评中看到此说明）：
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            placeholder="例如：请补充具体经文默想内容后再提交公开"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTargetNote(null)}>取消</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRejectNote}
            disabled={Boolean(reviewingId)}
          >
            确认驳回
          </Button>
        </DialogActions>
      </Dialog>

      {/* Note Preview Dialog */}
      {previewNote && (
        <Dialog
          open={Boolean(previewNote)}
          onClose={() => setPreviewNote(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, p: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 800 }}>
            {previewNote.title}
          </DialogTitle>
          <DialogContent>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              作者：{previewNote.userDisplayName || "读者"} · 出处：{previewNote.bookName} 第{previewNote.chapterNumber}章
            </Typography>
            {previewNote.verseText && (
              <Box sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: "action.hover", borderLeft: "3px solid #1976d2" }}>
                <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                  {cleanVerseQuoteText(previewNote.verseText)}
                </Typography>
              </Box>
            )}
            <Typography variant="body1" sx={{ whiteSpace: "pre-line", lineHeight: 1.7 }}>
              {previewNote.content}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewNote(null)}>关闭</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Toast Notification */}
      <Snackbar
        open={Boolean(toastMsg)}
        autoHideDuration={3000}
        onClose={() => setToastMsg("")}
        message={toastMsg}
      />
    </Container>
  );
};

export default AdminPortal;
